/**
 * AdminTrialInvites — single-use registration links for a trial WITHOUT
 * proforma (variant 5b). The customer registers through the normal form
 * (same consents and terms); the trial starts with auto-renewal off, so no
 * proforma is issued until the customer clicks "Kupuję — wystaw proformę".
 *
 * @see docs/specifications/2026-09-30-trial-invite-bez-proformy/spec.md
 */
import React, { useState } from "react";
import Layout from "../../components/Layout";
import { useBillingAdminBrand } from "../../hooks/useBillingAdminBrand";
import BillingBrandGuard from "./billing/BillingBrandGuard";
import BillingBrandSelector from "./billing/BillingBrandSelector";
import type {
  CreatedTrialInvite,
  TrialInvite,
} from "../../services/trialInviteApi";
import { useTrialInvites } from "./trial-invites/useTrialInvites";
import { inviteContext } from "./trial-invites/inviteContext";
import NewInviteForm, {
  type NewInviteValues,
} from "./trial-invites/NewInviteForm";
import CreatedInviteNotice from "./trial-invites/CreatedInviteNotice";
import InvitesTable from "./trial-invites/InvitesTable";
import RevokeFeedback from "./trial-invites/RevokeFeedback";

const REPLACE_UNCOPIED_LINK_CONFIRM =
  "Poprzedni link nie został skopiowany i nie da się go wyświetlić ponownie (pozostaje aktywny). Wygenerować nowy link?";

const revokeConfirm = (invite: TrialInvite) =>
  `Odwołać zaproszenie ${inviteContext(invite)}?\n\nLink przestanie działać od razu. Tej operacji nie można cofnąć.`;

const AdminTrialInvites: React.FC = () => {
  const { selectedBrand, brands, isLoading, selectBrand, isBrandRequired } =
    useBillingAdminBrand();
  const brandId = selectedBrand?.id ?? null;
  const {
    invitesQuery,
    createMutation,
    createError,
    revokeMutation,
    revokeFailure,
  } = useTrialInvites(brandId);

  const [created, setCreated] = useState<CreatedTrialInvite | null>(null);
  const [createdLinkCopied, setCreatedLinkCopied] = useState(false);

  const handleCreate = async (values: NewInviteValues): Promise<boolean> => {
    if (!brandId) return false;
    // The link is shown only once — do not drop an uncopied one silently.
    if (
      created &&
      !createdLinkCopied &&
      !window.confirm(REPLACE_UNCOPIED_LINK_CONFIRM)
    ) {
      return false;
    }
    try {
      const result = await createMutation.mutateAsync({ brandId, ...values });
      setCreated(result);
      setCreatedLinkCopied(false);
      return true;
    } catch {
      // Surfaced by the form through createError.
      return false;
    }
  };

  const handleRevoke = (invite: TrialInvite) => {
    if (!window.confirm(revokeConfirm(invite))) return;
    revokeMutation.mutate(invite);
  };

  // The retry repeats an already confirmed revoke — no second dialog.
  const retryRevoke = () => {
    if (revokeMutation.variables) revokeMutation.mutate(revokeMutation.variables);
  };

  return (
    <Layout>
      <BillingBrandGuard
        isBrandRequired={isBrandRequired}
        brands={brands}
        isLoading={isLoading}
        onSelectBrand={selectBrand}
      >
        <div className="space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">
                Zaproszenia na okres próbny bez proformy
              </h1>
              <p className="text-neutral-600 mt-1 text-sm">
                Jednorazowy link do rejestracji. Klient zakłada konto zwykłym
                formularzem (te same zgody i regulamin), ale okres próbny nie
                odnawia się automatycznie i system nie wystawi proformy, dopóki
                klient nie kliknie „Kupuję — wystaw proformę”.
              </p>
            </div>
            <BillingBrandSelector
              brands={brands}
              selectedBrand={selectedBrand}
              onSelectBrand={selectBrand}
            />
          </div>

          <NewInviteForm
            disabled={!brandId}
            isPending={createMutation.isPending}
            error={createError}
            onSubmit={handleCreate}
          />

          {created && (
            <CreatedInviteNotice
              key={created.url}
              created={created}
              onCopied={() => setCreatedLinkCopied(true)}
            />
          )}

          <RevokeFeedback
            revoked={
              revokeMutation.isSuccess ? (revokeMutation.variables ?? null) : null
            }
            failure={revokeFailure}
            onRetry={retryRevoke}
          />

          <InvitesTable
            invites={invitesQuery.data ?? []}
            isLoading={invitesQuery.isLoading}
            isError={invitesQuery.isError}
            onRetry={() => invitesQuery.refetch()}
            revokingId={
              revokeMutation.isPending
                ? (revokeMutation.variables?.id ?? null)
                : null
            }
            onRevoke={handleRevoke}
          />
        </div>
      </BillingBrandGuard>
    </Layout>
  );
};

export default AdminTrialInvites;
